/**
 * RailHintFooter
 *
 * One-line caption rendered at the bottom of the input rail's tab bodies
 * (Dynamic content + Expressions). Lives as its own primitive so the two
 * panels share the exact same shape — same border, padding, color,
 * font-size, and centred alignment — and any future polish (icon, kbd
 * pill, keyboard-first hint) only has to land here.
 *
 * Pure presentational: no state, no callbacks, no theming hooks beyond
 * Fluent tokens.
 */

import { Text, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  root: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke3),
    flexShrink: 0,
    textAlign: 'center',
  },
});

export interface RailHintFooterProps {
  /** Hint text — kept short so the single-line footer never wraps awkwardly. */
  children: ReactNode;
  /** Optional className passthrough for layout overrides. */
  className?: string;
}

export const RailHintFooter = ({ children, className }: RailHintFooterProps) => {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.root, className)}>
      <Text size={100}>{children}</Text>
    </div>
  );
};

RailHintFooter.displayName = 'RailHintFooter';
